import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Modal } from '@material-ui/core';
import { useAuth } from '../../AuthContext';
import { tokenIsExpired } from '../../authUtils';
import './Home.css';
import CopyrightIcon from '@mui/icons-material/Copyright';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { fetchData, SyncData, updateData, RentCollectionData } from '../../apiService';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonIcon from '@mui/icons-material/Person';
import BillPrint from '../pages/RentCollection/Bill';
import ReactDOM from 'react-dom';
import CircularProgress from '@mui/material/CircularProgress';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import DescriptionIcon from '@mui/icons-material/Description';
import InboxIcon from '@mui/icons-material/Inbox';

const Home = () => {
  const { isAuthenticated, companyId, userId, token, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    fetchtableData();
    setLoading(true);
  }, [isAuthenticated, companyId, userId, token, logout]);

  useEffect(() => {
    handleToDateChange();
  }, []);

  const fetchtableData = async () => {
    try {
      setLoading(true);
      const response = await RentCollectionData('RentCollectionsApi', toDate, companyId, token);
      if (response.responseCode === '1111') {
        setData(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching Customer Details:', error);
    } finally {
      setLoading(false);
    }
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  const handleToDateChange = async (event) => {
    const selectedDate = event?.target?.value;
    if (!selectedDate) return;
    setData([]);
    setToDate(selectedDate);
    try {
      const response = await RentCollectionData('RentCollectionsApi', selectedDate, companyId, token);
      if (response.responseCode === '1111') {
        if (response.response_data && response.response_data.length > 0) {
          setData(response.response_data);
        } else {
          setData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching Customer Details:', error);
    }
  };

  const handlePrint = async () => {
    try {
      setIsLoading(true);
      const customerId = InvoicedRooms.CustomerId;
      const response = await fetchData('RentCollectionsApi', `/RentCollectionPrint/${customerId}/${userId}/${toDate}`, token);
      const labelData = response.response_data;

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      document.body.appendChild(iframe);

      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

      ReactDOM.render(
        <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
        iframeDocument.body
      );

      iframeDocument.write('</body></html>');
      iframeDocument.close();
      iframe.contentWindow.print();

      await handleToDateChange({ target: { value: toDate } });
      try {
        await SyncData('RentCollectionsApi', `${customerId}`, token);
      } catch (updateError) {
        console.error('Error updating record:', updateError);
      }
    } catch (error) {
      console.error('Error printing Estimate:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const [open, setOpen] = useState(false);
  const [InvoicedRooms, setInvoicedRooms] = useState([]);

  const handleOpen = async (customerId) => {
    setOpen(true);
    try {
      const response = await fetchData('ReportApi', `/CustomerRoomDetails/${customerId}`, token);
      if (response.responseCode === '1111') {
        setInvoicedRooms(response.response_data);
      }
    } catch (error) {
      console.error('Error fetching item details:', error);
      if (error.response && error.response.status === 401) {
        logout();
      }
    }
  };

  const handleClose = () => setOpen(false);

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const handleInvoiceClick = () => setConfirmationOpen(true);
  const handleConfirmInvoice = () => {
    setConfirmationOpen(false);
    handlePrint();
  };
  const handleCancel = () => setConfirmationOpen(false);

  const [vacateconfirmationOpen, setVacateConfirmationOpen] = useState(false);
  const handleVacateClick = () => setVacateConfirmationOpen(true);
  const handleConfirmVacate = () => {
    setVacateConfirmationOpen(false);
    VacatingSubmit();
  };
  const handleVacateCancel = () => setVacateConfirmationOpen(false);

  const VacatingSubmit = async () => {
    setOpen(false);
    setIsLoading(true);
    const updatedFormData = {
      ID: InvoicedRooms.CustomerRelationId,
      RoomId: InvoicedRooms.RoomId,
      CustomerId: InvoicedRooms.CustomerId,
      CreatedDate: InvoicedRooms.Createddate,
      BillGenerateDate: InvoicedRooms.Rentdate,
      CustomerRentAmount: InvoicedRooms.RentAmount,
      CreatedBy: InvoicedRooms.UserId,
      UpdatedBy: userId,
      companyId: companyId,
    };

    try {
      await updateData('ReportApi', `${InvoicedRooms.CustomerRelationId}`, updatedFormData, token);
      setTimeout(() => window.location.reload(), 3000);
    } catch (error) {
      console.error('Error updating record:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      {loading && (
        <div className="loading-overlay">
          <CircularProgress size={40} className="loading-spinner" />
          <span className="loading-text">Loading...</span>
        </div>
      )}

      {/* Header */}
      <div className="dashboard-header">
        <h1 className="header-title">Dashboard</h1>
        <p className="header-subtitle">Rent Collection Management System</p>
        
        <div className="quick-actions">
          <Link to="/RentedCustomerIndex" className="action-btn invoice">
            <PaidIcon fontSize="small" /> Generate Invoice
          </Link>
          <Link to="/ReceiptCreate" className="action-btn receipt">
            <ReceiptIcon fontSize="small" /> Receipt
          </Link>
          <Link to="/CustomerCreate" className="action-btn customer">
            <PersonIcon fontSize="small" /> Customer
          </Link>
          <Link to="/RoomCreate" className="action-btn rooms">
            <MeetingRoomIcon fontSize="small" /> Rooms
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-card">
        <div className="section-header">
          <h2 className="section-title">Pending Invoices</h2>
          <input
            type="date"
            className="date-picker"
            value={toDate}
            onChange={handleToDateChange}
          />
        </div>

        {data && data.length > 0 ? (
          <div className="customers-grid">
            {data.map((item) => (
              <div
                key={item.Id}
                className="customer-card"
                onClick={() => handleOpen(item.CustomerId)}
              >
                <div className="customer-name">
                  <PersonIcon />
                  {item.CustomerName}
                </div>
                <div className="customer-details">
                  <div className="detail-row">
                    <HomeWorkIcon />
                    <span>{item.RoomName}</span>
                  </div>
                  <div className="rent-badge">₹ {item.RoomRent}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <InboxIcon />
            <p>No pending invoices for selected date</p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Modal open={open} onClose={handleClose}>
        <div className="modal-box">
          <div className="modal-header">
            <DescriptionIcon style={{ color: '#1e3a5f' }} />
            <h3 className="modal-title">Customer Details</h3>
          </div>
          
          {InvoicedRooms && (
            <div className="modal-body">
              <div className="info-row">
                <span className="info-label">Customer</span>
                <span className="info-value">{InvoicedRooms.Customer}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Room</span>
                <span className="info-value">{InvoicedRooms.RoomName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Rent Amount</span>
                <span className="info-value">₹ {InvoicedRooms.RentAmount}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Joining Date</span>
                <span className="info-value">{formatDate(InvoicedRooms.Joiningdate)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Leaving Date</span>
                <span className="info-value">
                  {InvoicedRooms.Leavingdate ? formatDate(InvoicedRooms.Leavingdate) : '—'}
                </span>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button className="modal-btn danger" onClick={handleInvoiceClick} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} color="inherit" /> : <><PaidIcon fontSize="small" /> Invoice</>}
            </button>
            <button className="modal-btn success" onClick={handleVacateClick} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} color="inherit" /> : <><ExitToAppIcon fontSize="small" /> Vacate</>}
            </button>
            <button className="modal-btn secondary" onClick={handleClose}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Invoice Confirmation */}
      <Modal open={confirmationOpen} onClose={handleCancel}>
        <div className="modal-box confirm-modal">
          <div className="confirm-icon">
            <PaidIcon />
          </div>
          <h3 className="confirm-title">Generate Invoice?</h3>
          <p className="confirm-message">Confirm generating rent invoice for this customer.</p>
          <div className="modal-actions">
            <button className="modal-btn primary" onClick={handleConfirmInvoice} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} color="inherit" /> : 'Yes, Generate'}
            </button>
            <button className="modal-btn secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Vacate Confirmation */}
      <Modal open={vacateconfirmationOpen} onClose={handleVacateCancel}>
        <div className="modal-box confirm-modal">
          <div className="confirm-icon">
            <ExitToAppIcon />
          </div>
          <h3 className="confirm-title">Confirm Vacating</h3>
          <p className="confirm-message">Are you sure you want to vacate this room?</p>
          <div className="modal-actions">
            <button className="modal-btn success" onClick={handleConfirmVacate} disabled={isLoading}>
              {isLoading ? <CircularProgress size={18} color="inherit" /> : 'Yes, Vacate'}
            </button>
            <button className="modal-btn secondary" onClick={handleVacateCancel}>Cancel</button>
          </div>
        </div>
      </Modal>

      {/* Watermark */}
    
    </div>
  );
};

export default Home;
